import { useEffect, useState } from "react";
import { Card } from "../components/Card";
import { Eyebrow } from "../components/Eyebrow";
import { HowTo, HowToStep } from "../components/HowTo";
import { MarketPriceLinks } from "../components/MarketPriceLinks";
import { formatYen } from "../lib/format";
import type { Industry, Item, PriceRange } from "../lib/types";

type Props = {
  industries: Industry[];
  items: Item[];
  onItemsChanged: () => void;
};

// マスタ管理: 上代レンジのCRUD + アイテム追加（＝相場検索カテゴリの充実）
export function MasterAdmin({ industries, items, onItemsChanged }: Props) {
  const [filterIndustry, setFilterIndustry] = useState("");
  const [filterItem, setFilterItem] = useState("");
  const [rows, setRows] = useState<PriceRange[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // 新規レンジ入力
  const [draft, setDraft] = useState<Partial<PriceRange>>({});

  // 新規アイテム
  const [newItem, setNewItem] = useState({ name: "", slug: "", default_unit: "個" });

  // 相場カバー率
  const [coverage, setCoverage] = useState<{
    covered: number;
    total: number;
    missing: { industry_label: string; item_name: string }[];
  } | null>(null);

  async function loadCoverage() {
    try {
      const res = await fetch("/api/masters?type=coverage");
      const data = await res.json();
      if (data.ok) {
        setCoverage({ covered: data.covered, total: data.total, missing: data.missing ?? [] });
      }
    } catch {
      // カバー率取得の失敗は致命的でないので無視
    }
  }

  async function load() {
    setLoading(true);
    try {
      const q = new URLSearchParams({ type: "price_ranges" });
      if (filterIndustry) q.set("industryId", filterIndustry);
      if (filterItem) q.set("itemId", filterItem);
      const res = await fetch(`/api/masters?${q.toString()}`);
      const data = await res.json();
      if (data.ok) setRows(data.price_ranges ?? []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndustry, filterItem]);

  useEffect(() => {
    loadCoverage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 既存行を編集する。draft に id が入っている間は upsert が UPDATE 側に回る（masters.ts）。
  // 価格改定で既存値を直すのが実務では一番多いので、一覧から直接開けるようにしている。
  function startEdit(r: PriceRange) {
    setDraft({ ...r });
    setMsg(null);
    document.getElementById("range-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  function cancelEdit() {
    setDraft({});
    setMsg(null);
  }

  async function saveRange() {
    if (!draft.industry_id || !draft.item_id || draft.lot_min == null || draft.competitor_lower == null || draft.competitor_upper == null) {
      setMsg("⚠ 業界・アイテム・ロット下限・競合下限・競合上限は必須です");
      return;
    }
    setMsg("保存中…");
    const res = await fetch("/api/masters", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "price_range", action: "upsert", ...draft }),
    });
    const data = await res.json();
    if (data.ok) {
      setMsg(draft.id ? "✓ 上代レンジを更新しました" : "✓ 上代レンジを登録しました");
      setDraft({});
      load();
      loadCoverage();
    } else {
      setMsg("⚠ " + (data.error ?? "保存失敗"));
    }
  }

  // 相場は調べ直すコストが高いので、消す前に何を消すのか見せて確認する。
  async function deleteRange(id?: string, r?: PriceRange) {
    if (!id) return;
    const what = r
      ? `${r.industry_label} × ${r.item_name}（${r.lot_label ?? `${r.lot_min}〜${r.lot_max ?? ""}`}）`
      : "この行";
    if (!window.confirm(`${what} の上代レンジを削除します。よろしいですか？`)) return;
    if (draft.id === id) setDraft({}); // 編集中の行を消したらフォームも閉じる
    const res = await fetch("/api/masters", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "price_range", action: "delete", id }),
    });
    const data = await res.json();
    if (data.ok) {
      load();
      loadCoverage();
    }
  }

  async function createItem() {
    if (!newItem.name || !newItem.slug) {
      setMsg("⚠ アイテム名と slug が必要です");
      return;
    }
    const res = await fetch("/api/masters", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "item", action: "create", ...newItem }),
    });
    const data = await res.json();
    if (data.ok) {
      setMsg(`✓ アイテム「${newItem.name}」を追加しました`);
      setNewItem({ name: "", slug: "", default_unit: "個" });
      onItemsChanged();
    } else {
      setMsg("⚠ " + (data.error ?? "追加失敗"));
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <Eyebrow>Master — 相場カテゴリ管理</Eyebrow>
        <h2 className="text-xl font-semibold tracking-tight mt-1">マスタ管理</h2>
        <p className="text-sm text-ink-muted mt-1 max-w-[60ch]">
          上代レンジ（市場×アイテム×ロット帯の競合相場と原価）を登録・編集します。ここを充実させると「提案を作る」の見積もり精度とカバー範囲が上がります。
        </p>
      </div>

      <HowTo title="使い方 — 相場が「未登録」と出たときにやること">
        <ol className="space-y-3">
          <HowToStep n={1}>
            下の<b>「未登録の組合せ」</b>を開く。ここに出ているものが、いま相場が入っていない市場×アイテムです。
            <span className="block text-xs mt-0.5">何を入れるか考えなくてよく、このリストを埋めていけば充実します。</span>
          </HowToStep>
          <HowToStep n={2}>
            <b>普段どおり調べる。</b>やり方を変える必要はありません。
            <span className="block text-xs mt-0.5">
              例）ご当地・観光地×マグネット → コーエイさんのHP ／ 水族館×アクキー → 「アクリルキーホルダー 水族館」で検索して施設のオンラインショップを見比べる
            </span>
          </HowToStep>
          <HowToStep n={3}>
            見るのは<b>施設のオンラインショップなど、実際に売っている値段（上代）</b>です。卸の仕入れ値ではありません。
          </HowToStep>
          <HowToStep n={4}>
            下のフォームに<b>上代の下限・上限</b>を入れて保存。
            <span className="block text-xs mt-0.5">
              1つの値に絞らなくてOK。安い方と高い方の幅で入れてください。原価は分かれば入力、<b>空でも保存できます</b>。
            </span>
          </HowToStep>
          <HowToStep n={5}>
            次に同じ組合せを選んだ人は<b>もう調べなくて済みます。</b>これが積み上がる部分です。
          </HowToStep>
        </ol>
        <p className="mt-4 border-t border-ink/10 pt-3 text-xs">
          <b>幅で入れてよい理由：</b>同じアクリルキーホルダーでも、水族館ECの実測で 1,540〜3,850円 と仕様差で幅がありました。
          サンプルが増えると中央値や上下位を自動で計算するので、<b>1件目から正確でなくても後から揃います。</b>
        </p>
        <p className="mt-2 text-xs">
          <b>値段が変わったとき：</b>「登録済み上代レンジ」の一覧で<b>「編集」</b>を押すと、
          今の値がそのまま下のフォームに入ります。直したいところだけ変えて<b>「この内容で更新する」</b>を押してください。
          <span className="block mt-0.5">
            相場は動くものなので、古くなったら遠慮なく上書きしてください（誰がいつ直したかは記録されます）。
          </span>
        </p>
      </HowTo>

      {/* 相場カバー率 */}
      {coverage && (
        <Card className="p-6">
          <div className="flex items-baseline gap-3">
            <p className="text-sm font-medium">相場カバー率</p>
            <p className="text-2xl font-semibold tracking-tight">
              {coverage.covered}/{coverage.total}
              <span className="text-base text-ink-muted ml-1">
                （{coverage.total > 0 ? Math.round((coverage.covered / coverage.total) * 100) : 0}%）
              </span>
            </p>
          </div>
          <p className="text-xs text-ink-muted mt-1 max-w-[60ch]">
            未登録の組合せは参考値（他市場の同アイテム平均）で見積もられます。正式な値の登録を優先してください。
          </p>
          {coverage.missing.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs text-ink-muted cursor-pointer select-none">
                未登録の組合せ（{coverage.missing.length}件）
              </summary>
              <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-ink-muted">
                {coverage.missing.map((m, i) => (
                  <li key={i}>
                    {m.industry_label} × {m.item_name}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </Card>
      )}

      {msg && <p className="text-sm text-ink-subtle">{msg}</p>}

      {/* フィルタ */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="block">
            <span className="text-sm font-medium">市場で絞り込み</span>
            <select value={filterIndustry} onChange={(e) => setFilterIndustry(e.target.value)}
              className="mt-1 w-full ring-1 ring-ink/15 rounded px-3 py-2 text-sm bg-white">
              <option value="">すべて</option>
              {industries.map((i) => <option key={i.id} value={i.id}>{i.sub_name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium">アイテムで絞り込み</span>
            <select value={filterItem} onChange={(e) => setFilterItem(e.target.value)}
              className="mt-1 w-full ring-1 ring-ink/15 rounded px-3 py-2 text-sm bg-white">
              <option value="">すべて</option>
              {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </label>
        </div>
      </Card>

      {/* 一覧 */}
      <Card className="p-6">
        <p className="text-sm font-medium mb-3">
          登録済み上代レンジ {loading ? "…" : `（${rows.length}件）`}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-muted border-b border-ink/10">
                <th className="py-2 font-medium">市場</th>
                <th className="py-2 font-medium">アイテム</th>
                <th className="py-2 font-medium">ロット帯</th>
                <th className="py-2 font-medium text-right">競合レンジ</th>
                <th className="py-2 font-medium text-right">原価</th>
                <th className="py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const editing = draft.id === r.id;
                return (
                  <tr
                    key={r.id}
                    className={`border-b border-ink/5 ${editing ? "bg-amber-50" : ""}`}
                  >
                    <td className="py-2">{r.industry_label}</td>
                    <td className="py-2">{r.item_name}</td>
                    <td className="py-2">{r.lot_label ?? `${r.lot_min}〜${r.lot_max ?? ""}`}</td>
                    <td className="py-2 text-right">{formatYen(r.competitor_lower)}〜{formatYen(r.competitor_upper)}</td>
                    <td className="py-2 text-right text-ink-muted">
                      {r.cost_lower != null || r.cost_upper != null
                        ? `${formatYen(r.cost_lower)}〜${formatYen(r.cost_upper)}`
                        : "—"}
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => startEdit(r)}
                        className="text-xs font-medium text-ink-muted hover:text-ink"
                      >
                        {editing ? "編集中" : "編集"}
                      </button>
                      <span className="mx-2 text-ink/20">|</span>
                      <button type="button" onClick={() => deleteRange(r.id, r)}
                        className="text-xs text-ink-muted hover:text-red-600">削除</button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && !loading && (
                <tr><td colSpan={6} className="py-4 text-center text-ink-muted">レンジがありません</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* レンジ登録・編集 */}
      <Card id="range-form" className={`p-6 ${draft.id ? "ring-2 ring-amber-400" : ""}`}>
        <div className="mb-3 flex items-center justify-between gap-3">
          <p className="text-sm font-medium">
            {draft.id ? "上代レンジを編集" : "上代レンジを追加"}
          </p>
          {draft.id && (
            <div className="flex items-center gap-3">
              <span className="text-xs text-ink-muted">
                {rows.find((r) => r.id === draft.id)?.industry_label} ×{" "}
                {rows.find((r) => r.id === draft.id)?.item_name} を編集中
              </span>
              <button
                type="button"
                onClick={cancelEdit}
                className="rounded-full bg-white px-3 py-1 text-xs font-medium ring-1 ring-ink/15 hover:ring-ink/40"
              >
                編集をやめる
              </button>
            </div>
          )}
        </div>
        <MarketPriceLinks
          itemName={items.find((i) => i.id === draft.item_id)?.name}
          marketName={industries.find((i) => i.id === draft.industry_id)?.sub_name}
          className="mb-3"
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <select value={draft.industry_id ?? ""} onChange={(e) => setDraft((d) => ({ ...d, industry_id: e.target.value }))}
            className="ring-1 ring-ink/15 rounded px-3 py-2 text-sm bg-white">
            <option value="">市場…</option>
            {industries.map((i) => <option key={i.id} value={i.id}>{i.sub_name}</option>)}
          </select>
          <select value={draft.item_id ?? ""} onChange={(e) => setDraft((d) => ({ ...d, item_id: e.target.value }))}
            className="ring-1 ring-ink/15 rounded px-3 py-2 text-sm bg-white">
            <option value="">アイテム…</option>
            {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
          <input type="number" placeholder="ロット下限" value={draft.lot_min ?? ""} onChange={(e) => setDraft((d) => ({ ...d, lot_min: Number(e.target.value) }))}
            className="ring-1 ring-ink/15 rounded px-3 py-2 text-sm" />
          <input type="number" placeholder="ロット上限(空=∞)" value={draft.lot_max ?? ""} onChange={(e) => setDraft((d) => ({ ...d, lot_max: e.target.value === "" ? null : Number(e.target.value) }))}
            className="ring-1 ring-ink/15 rounded px-3 py-2 text-sm" />
          <input type="number" placeholder="競合下限" value={draft.competitor_lower ?? ""} onChange={(e) => setDraft((d) => ({ ...d, competitor_lower: Number(e.target.value) }))}
            className="ring-1 ring-ink/15 rounded px-3 py-2 text-sm" />
          <input type="number" placeholder="競合上限" value={draft.competitor_upper ?? ""} onChange={(e) => setDraft((d) => ({ ...d, competitor_upper: Number(e.target.value) }))}
            className="ring-1 ring-ink/15 rounded px-3 py-2 text-sm" />
          <input type="number" placeholder="原価下限(任意)" value={draft.cost_lower ?? ""} onChange={(e) => setDraft((d) => ({ ...d, cost_lower: e.target.value === "" ? null : Number(e.target.value) }))}
            className="ring-1 ring-ink/15 rounded px-3 py-2 text-sm" />
          <input type="number" placeholder="原価上限(任意)" value={draft.cost_upper ?? ""} onChange={(e) => setDraft((d) => ({ ...d, cost_upper: e.target.value === "" ? null : Number(e.target.value) }))}
            className="ring-1 ring-ink/15 rounded px-3 py-2 text-sm" />
        </div>
        <button type="button" onClick={saveRange}
          className="mt-3 px-4 py-2 rounded-full bg-ink text-white text-sm font-medium hover:bg-ink/90">
          {draft.id ? "この内容で更新する" : "レンジを保存"}
        </button>
      </Card>

      {/* アイテム追加 */}
      <Card className="p-6">
        <p className="text-sm font-medium mb-1">アイテム（カテゴリ）を追加</p>
        <p className="text-xs text-ink-muted mb-3">相場検索・提案で選べるアイテムを増やします。</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input placeholder="アイテム名（例: パーカー）" value={newItem.name} onChange={(e) => setNewItem((n) => ({ ...n, name: e.target.value }))}
            className="ring-1 ring-ink/15 rounded px-3 py-2 text-sm" />
          <input placeholder="slug（例: hoodie 半角英数）" value={newItem.slug} onChange={(e) => setNewItem((n) => ({ ...n, slug: e.target.value }))}
            className="ring-1 ring-ink/15 rounded px-3 py-2 text-sm" />
          <input placeholder="単位（例: 枚）" value={newItem.default_unit} onChange={(e) => setNewItem((n) => ({ ...n, default_unit: e.target.value }))}
            className="ring-1 ring-ink/15 rounded px-3 py-2 text-sm" />
        </div>
        <button type="button" onClick={createItem}
          className="mt-3 px-4 py-2 rounded-full ring-1 ring-ink/15 bg-white text-sm font-medium hover:ring-ink/40">
          ＋ アイテムを追加
        </button>
      </Card>
    </div>
  );
}
